

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewDepartureDateModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        trip_id: '',
        option_id: '',
        sub_option_id: '',
        departure_date: '',
        return_date: '',
        hold: 0,
        sold: 0,
        minimum_pax: '',
        maximum_pax: '',
        allocation_secured: false,
        total_allocation: '',
        book_by_date: '',
        trip_title_snapshot: '',
        departure_city: '',
        high_season_surcharge: '',
        bc_due_date: '',
        tp_due_date: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [trips, setTrips] = useState([]);
    const [options, setOptions] = useState([]);
    const [subOptions, setSubOptions] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchTrips();
        }
    }, [isOpen]);

    useEffect(() => {
        if (formData.trip_id) {
            fetchOptions(formData.trip_id);
        } else {
            setOptions([]);
            setSubOptions([]);
        }
    }, [formData.trip_id]);

    useEffect(() => {
        if (formData.option_id) {
            fetchSubOptions(formData.option_id);
        } else {
            setSubOptions([]);
        }
    }, [formData.option_id]);

    const fetchTrips = async () => {
        setLoadingData(true);
        try {
            const response = await axiosInstance.get('/trips/');
            setTrips(response.data);
        } catch (error) {
            console.error('Error fetching trips:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const fetchOptions = async (tripId) => {
        try {
            const response = await axiosInstance.get('/trip-options/');
            // Filter by tripId if the API supports it, otherwise filter here
            const filtered = response.data.filter(o => o.trip_id === parseInt(tripId));
            setOptions(filtered);
        } catch (error) {
            console.error('Error fetching options:', error);
        }
    };

    const fetchSubOptions = async (optionId) => {
        try {
            const response = await axiosInstance.get('/trip-sub-options/');
            const filtered = response.data.filter(s => s.option_id === parseInt(optionId));
            setSubOptions(filtered);
        } catch (error) {
            console.error('Error fetching sub-options:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name/ID is required';
        if (!formData.trip_id) errors.trip_id = 'Trip is required';
        if (!formData.departure_date) errors.departure_date = 'Departure Date is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                trip_id: parseInt(formData.trip_id),
                option_id: formData.option_id ? parseInt(formData.option_id) : null,
                sub_option_id: formData.sub_option_id ? parseInt(formData.sub_option_id) : null,
                hold: parseInt(formData.hold || 0),
                sold: parseInt(formData.sold || 0),
                minimum_pax: formData.minimum_pax ? parseInt(formData.minimum_pax) : null,
                maximum_pax: formData.maximum_pax ? parseInt(formData.maximum_pax) : null,
                total_allocation: formData.total_allocation ? parseInt(formData.total_allocation) : null,
                high_season_surcharge: formData.high_season_surcharge ? parseFloat(formData.high_season_surcharge) : null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            // Reset form
            setFormData({
                name: '', trip_id: '', option_id: '', sub_option_id: '',
                departure_date: '', return_date: '', hold: 0, sold: 0,
                minimum_pax: '', maximum_pax: '', allocation_secured: false,
                total_allocation: '', book_by_date: '', trip_title_snapshot: '',
                departure_city: '', high_season_surcharge: '', bc_due_date: '', tp_due_date: ''
            });
        } catch (error) {
            console.error('Error saving Departure Date:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Departure Date',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Departure Date</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Departure ID/Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name || "e.g., DEP-0001"}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.trip_id} required>
                                        <InputLabel>Trip</InputLabel>
                                        <Select
                                            name="trip_id"
                                            value={formData.trip_id}
                                            onChange={handleInputChange}
                                            label="Trip"

                                        >
                                            <MenuItem value=""><em>Select a Trip</em></MenuItem>
                                            {trips.map(t => (
                                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.trip_id && <MDTypography variant="caption" color="error">{formErrors.trip_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Option (Optional)</InputLabel>
                                        <Select
                                            name="option_id"
                                            value={formData.option_id}
                                            onChange={handleInputChange}
                                            label="Option (Optional)"

                                            disabled={!formData.trip_id}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {options.map(o => (
                                                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Sub-Option (Optional)</InputLabel>
                                        <Select
                                            name="sub_option_id"
                                            value={formData.sub_option_id}
                                            onChange={handleInputChange}
                                            label="Sub-Option (Optional)"

                                            disabled={!formData.option_id}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {subOptions.map(s => (
                                                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Departure Date"
                                        name="departure_date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.departure_date}
                                        onChange={handleInputChange}
                                        error={!!formErrors.departure_date}
                                        helperText={formErrors.departure_date}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Return Date"
                                        name="return_date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.return_date}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Total Allocation"
                                        name="total_allocation"
                                        type="number"
                                        value={formData.total_allocation}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Hold"
                                        name="hold"
                                        type="number"
                                        value={formData.hold}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Sold"
                                        name="sold"
                                        type="number"
                                        value={formData.sold}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Available"
                                        value={(formData.total_allocation || 0) - (formData.sold || 0) - (formData.hold || 0)}
                                        disabled
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Min Pax"
                                        name="minimum_pax"
                                        type="number"
                                        value={formData.minimum_pax}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Max Pax"
                                        name="maximum_pax"
                                        type="number"
                                        value={formData.maximum_pax}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Departure City"
                                        name="departure_city"
                                        value={formData.departure_city}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="High Season Surcharge"
                                        name="high_season_surcharge"
                                        type="number"
                                        value={formData.high_season_surcharge}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Book By Date"
                                        name="book_by_date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.book_by_date}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="BC Due Date"
                                        name="bc_due_date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.bc_due_date}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        helperText="Typically 60 days before departure"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="TP Due Date"
                                        name="tp_due_date"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={formData.tp_due_date}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Checkbox checked={formData.allocation_secured} onChange={handleInputChange} name="allocation_secured" />}
                                        label="Allocation Secured"
                                        sx={{ mt: 2 }}
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Departure'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewDepartureDateModal;
